import { useCallback, useEffect, useState } from "react";
import { Check, ExternalLink, Loader2, PlayCircle, RefreshCw } from "lucide-react";

import {
  type CodingSystem,
  getLatestInvestigation,
  type Investigation,
  type InvestigationFinding,
  runInvestigation,
} from "@/api/investigations";
import { getLangfuseTraceUrl } from "@/ai-coding-outputs/langfuseUrl";
import { getInvestigatorMastraRunUrl } from "@/ai-coding-outputs/mastraWorkflowRunUrl";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { MarkdownRenderer } from "@/components/ui/markdown-renderer";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";

const ALL_CODING_SYSTEMS: CodingSystem[] = ["ICD-10", "OPCS-4"];

interface InvestigatorPanelProps {
  encounterId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

type LoadState =
  | { kind: "idle" }
  | { kind: "loading" }
  | { kind: "loaded"; investigation: Investigation | null; findings: InvestigationFinding[] }
  | { kind: "error"; message: string };

export function InvestigatorPanel({ encounterId, open, onOpenChange }: InvestigatorPanelProps) {
  const [loadState, setLoadState] = useState<LoadState>({ kind: "idle" });
  const [isRunning, setIsRunning] = useState(false);
  const [runError, setRunError] = useState<string | null>(null);
  const [selectedCodingSystems, setSelectedCodingSystems] = useState<CodingSystem[]>(ALL_CODING_SYSTEMS);
  /** Run id of the most recent run kicked off from this panel — used to render a Mastra link while the workflow is in flight. */
  const [activeRunId, setActiveRunId] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (!encounterId) return;
    setLoadState({ kind: "loading" });
    try {
      const data = await getLatestInvestigation(encounterId);
      setLoadState({ kind: "loaded", investigation: data.investigation, findings: data.findings });
    } catch (error) {
      setLoadState({ kind: "error", message: error instanceof Error ? error.message : "Unknown error" });
    }
  }, [encounterId]);

  // Load the latest investigation when the panel opens for a new encounter.
  useEffect(() => {
    if (!open || !encounterId) return;
    // Drop the in-flight run id from a prior encounter so its link doesn't leak across.
    setActiveRunId(null);
    setRunError(null);
    void refresh();
  }, [open, encounterId, refresh]);

  const handleRun = useCallback(async () => {
    if (!encounterId || selectedCodingSystems.length === 0) return;
    const runId = crypto.randomUUID();
    setActiveRunId(runId);
    setIsRunning(true);
    setRunError(null);
    try {
      await runInvestigation(encounterId, { codingSystems: selectedCodingSystems, runId });
      await refresh();
    } catch (error) {
      setRunError(error instanceof Error ? error.message : "Unknown error");
    } finally {
      setIsRunning(false);
    }
  }, [encounterId, refresh, selectedCodingSystems]);

  const investigation = loadState.kind === "loaded" ? loadState.investigation : null;
  const findings = loadState.kind === "loaded" ? loadState.findings : [];
  const investigatorTraceUrl = investigation?.investigatorLangfuseTraceId
    ? getLangfuseTraceUrl(investigation.investigatorLangfuseTraceId)
    : null;
  // Prefer the in-flight run id (so the link works while the workflow is running),
  // otherwise fall back to the persisted run id from the latest investigation.
  const mastraRunUrlRunId = activeRunId ?? investigation?.investigatorRunId ?? null;
  const mastraRunUrl = mastraRunUrlRunId ? getInvestigatorMastraRunUrl(mastraRunUrlRunId) : null;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-xl overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">Coding Investigator</SheetTitle>
          <SheetDescription>
            AI-as-judge review of AI vs. human coding discrepancies for this encounter.
          </SheetDescription>
        </SheetHeader>

        <div className="mt-4 flex items-center gap-2 flex-wrap">
          <ToggleGroup
            type="multiple"
            size="sm"
            variant="outline"
            value={selectedCodingSystems}
            onValueChange={(value) => setSelectedCodingSystems(value as CodingSystem[])}
            aria-label="Coding systems to review"
          >
            {ALL_CODING_SYSTEMS.map((system) => {
              const isSelected = selectedCodingSystems.includes(system);
              return (
                <ToggleGroupItem
                  key={system}
                  value={system}
                  aria-label={system}
                  className="gap-2 data-[state=on]:border-primary data-[state=on]:bg-primary/5 data-[state=on]:text-primary"
                >
                  <span
                    aria-hidden
                    className={
                      "inline-flex h-4 w-4 items-center justify-center rounded-full border " +
                      (isSelected
                        ? "border-primary bg-primary text-primary-foreground"
                        : "border-muted-foreground/40 bg-transparent")
                    }
                  >
                    {isSelected && <Check className="h-3 w-3" strokeWidth={3} />}
                  </span>
                  {system}
                </ToggleGroupItem>
              );
            })}
          </ToggleGroup>
          <Button
            onClick={handleRun}
            disabled={!encounterId || isRunning || selectedCodingSystems.length === 0}
            size="sm"
          >
            {isRunning ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : investigation ? (
              <RefreshCw className="h-4 w-4 mr-2" />
            ) : (
              <PlayCircle className="h-4 w-4 mr-2" />
            )}
            {isRunning ? "Running…" : investigation ? "Re-run" : "Run investigator"}
          </Button>
          <Button onClick={refresh} disabled={!encounterId || loadState.kind === "loading"} variant="outline" size="sm">
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
          {mastraRunUrl && (
            <Button asChild variant="outline" size="sm">
              <a href={mastraRunUrl} target="_blank" rel="noreferrer">
                <ExternalLink className="h-4 w-4 mr-2" />
                View run
              </a>
            </Button>
          )}
          {investigatorTraceUrl && (
            <Button asChild variant="ghost" size="sm">
              <a href={investigatorTraceUrl} target="_blank" rel="noreferrer">
                <ExternalLink className="h-4 w-4 mr-2" />
                Langfuse trace
              </a>
            </Button>
          )}
        </div>

        {runError && (
          <div className="mt-3 text-sm text-destructive border border-destructive/30 bg-destructive/5 rounded-md p-2">
            {runError}
          </div>
        )}

        <div className="mt-6">
          {loadState.kind === "idle" && <p className="text-sm text-muted-foreground">No investigation loaded yet.</p>}
          {loadState.kind === "loading" && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading latest investigation…
            </div>
          )}
          {loadState.kind === "error" && <p className="text-sm text-destructive">{loadState.message}</p>}
          {loadState.kind === "loaded" && !investigation && (
            <p className="text-sm text-muted-foreground">
              No investigation has been run for this encounter yet. Click <strong>Run investigator</strong> to start
              one.
            </p>
          )}
          {loadState.kind === "loaded" && investigation && (
            <InvestigationBody investigation={investigation} findings={findings} />
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

interface InvestigationBodyProps {
  investigation: Investigation;
  findings: InvestigationFinding[];
}

function InvestigationBody({ investigation, findings }: InvestigationBodyProps) {
  return (
    <div className="space-y-5">
      <div className="text-xs text-muted-foreground flex flex-wrap gap-x-3 gap-y-1">
        <span>
          Ran: <span className="font-mono">{new Date(investigation.createdAt).toLocaleString()}</span>
        </span>
        <span>
          Trigger: <Badge variant="outline">{investigation.triggeredBy}</Badge>
        </span>
        {investigation.investigatorRunId && (
          <span className="font-mono">Run {investigation.investigatorRunId.slice(0, 8)}…</span>
        )}
      </div>

      {investigation.errorMessage && (
        <div className="text-sm text-destructive border border-destructive/30 bg-destructive/5 rounded-md p-2">
          {investigation.errorMessage}
        </div>
      )}

      {investigation.summaryMarkdown ? (
        <div>
          <h4 className="text-sm font-semibold mb-2">Summary</h4>
          <div className="rounded-md border border-border bg-muted/30 p-3">
            <MarkdownRenderer content={investigation.summaryMarkdown} />
          </div>
        </div>
      ) : null}

      <div>
        <h4 className="text-sm font-semibold mb-2">Findings ({findings.length})</h4>
        {findings.length === 0 ? (
          <p className="text-xs text-muted-foreground">No discrepancies flagged for this run.</p>
        ) : (
          <ul className="space-y-3">
            {findings.map((f) => (
              <FindingCard key={f.id} finding={f} />
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function FindingCard({ finding }: { finding: InvestigationFinding }) {
  return (
    <li className="rounded-md border border-border p-3 space-y-2 bg-white">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <code className="text-sm font-mono font-semibold">{finding.code}</code>
          <Badge variant="outline">{finding.codingSystem}</Badge>
          <Badge variant={kindBadgeVariant(finding.discrepancyKind)}>{finding.discrepancyKind}</Badge>
        </div>
        <Badge variant={confidenceBadgeVariant(finding.confidence)}>{finding.confidence} confidence</Badge>
      </div>

      <div className="text-xs text-muted-foreground flex flex-wrap gap-x-3 gap-y-1">
        <span>
          Step: <code className="text-xs font-mono text-foreground">{finding.suspectedStep}</code>
        </span>
        {finding.suspectedPromptName && (
          <span>
            Prompt:{" "}
            <code className="text-xs font-mono text-foreground">
              {finding.suspectedPromptName}
              {finding.suspectedPromptVersion != null ? ` v${finding.suspectedPromptVersion}` : ""}
            </code>
          </span>
        )}
        <span>
          Root cause: <Badge variant="outline">{finding.rootCauseCategory}</Badge>
        </span>
        {finding.knownPatternRef && <span className="italic">pattern: {finding.knownPatternRef}</span>}
      </div>

      <MarkdownRenderer content={finding.suggestedFix} />
    </li>
  );
}

function kindBadgeVariant(kind: InvestigationFinding["discrepancyKind"]): "default" | "destructive" | "secondary" {
  switch (kind) {
    case "overcoded":
      return "destructive";
    case "undercoded":
      return "default";
    case "duplicated":
      return "secondary";
    case "wrong":
      return "destructive";
  }
}

function confidenceBadgeVariant(
  confidence: InvestigationFinding["confidence"],
): "default" | "destructive" | "secondary" {
  switch (confidence) {
    case "high":
      return "default";
    case "medium":
      return "secondary";
    case "low":
      return "secondary";
  }
}
